import React, { useState } from 'react'
import { FormControl, FormLabel, Radio, RadioGroup } from '@chakra-ui/react'
import {
  type MappingDetails,
  type TenantCommodity,
  type UpdateCommodityMapping,
  useUpdateTenantCommodityMapping,
} from 'src/api/queries/microservices/platform-markets-commodity-config'
import { Box, Button, HStack, Row, Stack, Text } from 'src/ui'
import { useToast } from 'src/utils/toast'
import TenantCommodityPicker from '../commodity-symbol-mappings/tenant-commodity-picker'

type MappingScope = 'global' | 'perLocation'

type MappingEditorFormProps = {
  mapping: MappingDetails
  tenantId: string
  canEdit: boolean
  onClose: () => void
}

export default function MappingEditorForm({
  mapping,
  tenantId,
  canEdit,
  onClose,
}: MappingEditorFormProps) {
  const toast = useToast()
  const { mutateAsync: updateMapping, isPending } = useUpdateTenantCommodityMapping()
  const locations = mapping.locationMappings
  const globalCommodity =
    mapping.mapping.type === 'global' ? mapping.mapping.data.tenantCommodity : null

  const [scope, setScope] = useState<MappingScope>(
    mapping.mapping.type === 'perLocation' ? 'perLocation' : 'global'
  )
  const [tenantCommodity, setTenantCommodity] = useState<TenantCommodity | null>(globalCommodity)
  const [locationCommodities, setLocationCommodities] = useState<
    Record<string, TenantCommodity | null>
  >(() =>
    locations.reduce<Record<string, TenantCommodity | null>>((result, location) => {
      result[location.locationId] = location.tenantCommodity
      return result
    }, {})
  )

  const handleSave = async () => {
    if (!canEdit || isPending) return

    const selectedMappings = locations.flatMap((location) => {
      const commodity = locationCommodities[location.locationId]
      return commodity ? [{ locationId: location.locationId, tenantCommodityId: commodity.id }] : []
    })

    const nextMapping: UpdateCommodityMapping =
      scope === 'global' && tenantCommodity
        ? { type: 'global', data: { tenantCommodityId: tenantCommodity.id } }
        : scope === 'perLocation' && selectedMappings.length
          ? { type: 'perLocation', data: { mappings: selectedMappings } }
          : { type: 'unmapped', data: {} }

    try {
      await updateMapping({ commodityId: mapping.commodityId, mapping: nextMapping })
      toast({ description: `Saved mapping for ${mapping.commodityName}` })
      onClose()
    } catch {
      toast({ status: 'error', description: 'Failed to save commodity mapping.' })
    }
  }

  const isDisabled = !canEdit || isPending

  return (
    <Box bg="white" borderRadius={3} boxShadow="md" p={6}>
      <Row justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="lg" fontWeight="600">
          {mapping.commodityName}
        </Text>
        <HStack>
          <Button size="sm" variant="ghost" isDisabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="primary"
            isLoading={isPending}
            isDisabled={isDisabled}
            onClick={handleSave}
          >
            Save
          </Button>
        </HStack>
      </Row>

      <FormControl as="fieldset" mb={5}>
        <FormLabel as="legend" fontWeight="600">
          Mapping scope
        </FormLabel>
        <RadioGroup value={scope} onChange={(value) => setScope(value as MappingScope)}>
          <HStack spacing={6}>
            <Radio value="global" isDisabled={isDisabled}>
              Tenant
            </Radio>
            <Radio value="perLocation" isDisabled={isDisabled || !locations.length}>
              Per location
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      {scope === 'global' && (
        <FormControl>
          <FormLabel>Tenant commodity</FormLabel>
          <TenantCommodityPicker
            id={`tenant-commodity-${mapping.commodityId}`}
            tenantId={tenantId}
            value={tenantCommodity}
            placeholder="Select a tenant commodity, or leave empty to clear"
            isDisabled={isDisabled}
            onChange={setTenantCommodity}
          />
        </FormControl>
      )}
      {scope === 'perLocation' && (
        <Stack spacing={3}>
          <Text fontWeight="600">Tenant commodity by location</Text>
          {!locations.length && <Text>No eligible platform locations found.</Text>}
          {locations.map((location) => (
            <HStack key={location.locationId} alignItems="center">
              <Text width="180px" flexShrink={0}>
                {location.locationName}
              </Text>
              <Box flex="1" minWidth={0}>
                <TenantCommodityPicker
                  id={`tenant-commodity-${mapping.commodityId}-${location.locationId}`}
                  tenantId={tenantId}
                  value={locationCommodities[location.locationId] ?? null}
                  placeholder="Unmapped"
                  isDisabled={isDisabled}
                  onChange={(commodity) =>
                    setLocationCommodities((current) => ({
                      ...current,
                      [location.locationId]: commodity,
                    }))
                  }
                />
              </Box>
            </HStack>
          ))}
        </Stack>
      )}
    </Box>
  )
}
